/**
 * What counts as "just added", for the New shelf on Masterclasses.
 *
 * Pulled out of the render for one reason: every way this goes wrong is
 * silent. A date_added that does not parse makes an item permanently old, a
 * timezone slip makes it fresh for a day too long, and a shelf that is empty
 * when it should not be looks exactly like a shelf with nothing new. None of
 * that shows up as an error, so it gets a test instead.
 *
 * Deliberately NOT keyed off the hand-set `isNew` flag on the cards. Nothing
 * clears that flag and several carry it from June, so it means "was new once"
 * rather than "is new". A date cannot go stale on its own.
 */

/** How long something stays on the New shelf. "A few days", decided once. */
export const NEW_SHELF_DAYS = 7;

const DAY_MS = 86400000;

/**
 * True when `item` was added inside the window.
 *
 * An unparseable or missing date is treated as not fresh, which is the safe
 * direction: a real release that fails to appear on the shelf is a card in
 * its normal category, where a bad date treated as fresh would pin something
 * to the top of the page forever.
 */
export function isFreshlyAdded(item, days = NEW_SHELF_DAYS, now = Date.now()) {
  const t = item?.date_added ? new Date(item.date_added).getTime() : NaN;
  if (!Number.isFinite(t)) return false;
  // A future date is still fresh: scheduling a release for tomorrow should
  // not mean it skips the shelf entirely on the day it lands.
  return t >= now - days * DAY_MS;
}

/**
 * Splits items into the New shelf and everything else, newest first.
 *
 * Fresh items go in `fresh` and are REMOVED from `settled`, so each card
 * appears exactly once on the page. With a dozen masterclasses visible at a
 * glance, the same card in two rows reads as a bug rather than a feature.
 */
export function splitFresh(items, days = NEW_SHELF_DAYS, now = Date.now()) {
  const fresh = [];
  const settled = [];
  for (const i of items) (isFreshlyAdded(i, days, now) ? fresh : settled).push(i);
  fresh.sort((a, b) => new Date(b.date_added) - new Date(a.date_added));
  return { fresh, settled };
}
